import styled from "@emotion/styled";
import { debounce, InputAdornment, inputAdornmentClasses } from "@mui/material";
import { inputBaseClasses } from "@mui/material/InputBase";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { FC, ReactNode, useMemo, useState } from "react";
import { EIconNames } from "../types/iconNames";
import { IconButton, IconComponent } from ".";

export interface ISearchFieldProps
  extends Pick<
    TextFieldProps,
    | "variant"
    | "placeholder"
    | "value"
    | "sx"
    | "fullWidth"
    | "autoFocus"
    | "inputRef"
  > {
  onChange: (v: string) => void;
  onCrossClick?: () => void;
  isDebounced?: boolean;
  alwaysShowCrossButton?: boolean;
}

const StyledTextField = styled(TextField)<TextFieldProps>(({
  theme,
  variant,
}) => {
  if (variant === "standard") return {};

  return {
    [`& .${inputBaseClasses.root}`]: {
      paddingRight: theme.spacing(1),
      [`& .${outlinedInputClasses.notchedOutline}`]: {},
      ["& .search-icon"]: {
        fontSize: "20px",
      },
    },
    [`& .${inputAdornmentClasses.root}`]: {
      flexShrink: 0,
      color: theme.palette.grey[500],
    },
  };
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  flexShrink: 0,
  ["&:hover"]: {
    ["& .icons"]: { color: `${theme.palette.grey[600]}!important` },
  },
  ["&:active"]: {
    backgroundColor: theme.palette.grey[200],
    ["& .icons"]: { color: `${theme.palette.grey[700]}!important` },
  },
  ["& .icons"]: {
    fontSize: "20px!important",
    color: `${theme.palette.grey[500]}!important`,
  },
}));

export const SearchField: FC<ISearchFieldProps> = ({
  onCrossClick,
  onChange,
  isDebounced = true,
  alwaysShowCrossButton,
  value,
  ...restProps
}) => {
  const [inputValue, setInputValue] = useState(value);
  // need useMemo because we set to state and then loose debounce
  const debounceSearch = useMemo(() => debounce(onChange, 750), [onChange]);
  const search = (v: string) => {
    setInputValue(v);
    if (isDebounced) debounceSearch(v);
    else onChange(v);
  };

  const renderEndAdornment = (): ReactNode => {
    return (
      <>
        {onCrossClick && (alwaysShowCrossButton || !!inputValue) && (
          <StyledIconButton
            rounded
            variant="text"
            color="secondary"
            iconName="close"
            className="close-button"
            onClick={() => {
              setInputValue("");
              onCrossClick();
            }}
          />
        )}
      </>
    );
  };

  return (
    <StyledTextField
      value={inputValue}
      onChange={(e) => search(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconComponent
              className="search-icon"
              iconname={EIconNames.SEARCH}
            />
          </InputAdornment>
        ),
        endAdornment: renderEndAdornment(),
      }}
      {...restProps}
    />
  );
};

export default SearchField;
